<template>
  <div class="file-search__root">
    <div class="file-search__abs-container">
      <pui-file-search-input
        ref="searchInput"
        :items="inputSearchItems"
        :singleLine="!filtersAreOpen"
        :isSearchItemValid="isSearchItemValid"
        @searchClicked.stop="inputOnSearchClicked"
        @enter="inputOnEnter"
        @clearSelection="inputOnClearSelection"
        @addValue="inputOnAddValue"
        @removeItem="inputOnRemoveItem"
        @blur="inputOnBlur"
        class="file-search__input"
      />
      <pui-file-search-filters
        ref="filters"
        v-if="searchFilterReference"
        :reference="searchFilterReference"
        @opened="filtersOnOpened"
        @closed="filtersOnClosed"
      >
        <!-- External search filters -->
        <pui-file-search-form
          ref="externalFileSearchForm"
          v-if="showExternalSearchForm"
          @addAttributeValue="extFormOnAddAttributeValue"
          @updateAttributeValue="extFormOnUpdateAttributeValue"
          :retrieveFormDefinition="retrieveExtSearchFormDef"
        >
        </pui-file-search-form>

        <!-- Media search filters -->
        <pui-file-search-form
          ref="mediaFileSearchForm"
          @addAttributeValue="piivoFormOnAddAttributeValue"
          @updateAttributeValue="piivoFormOnUpdateAttributeValue"
          :retrieveFormDefinition="retrievePiivoFormDef"
        >
          <template #bodyAfter>
            <pui-file-search-preview
              ref="searchPreview"
              v-if="searchItems.length"
              :generateSearchQuery="generateSearchQuery"
              :searchItems="normalizedSearchItems"
              @searchForFile="onSearchForFileTrigger"
              @executeSearch="inputOnSearchClicked"
              @refreshedPreview="dirtySearchPreview = false"
            />
          </template>
        </pui-file-search-form>
      </pui-file-search-filters>
    </div>
  </div>
</template>

<script>
import Vue from 'vue';

import { DetailsAttributeFilters, SearchType } from '../../../constants/search';
import {
  extractSearchItemAttributeValue,
  isFilenameSearchPrefix,
  isSearchItemInlineFilter,
  getSearchItemDisplayValue,
} from '../../../helpers/fileSearchHelper';
import {
  checkAttributeFilterExists,
  getCorrespondingAttributeFilter,
} from '../../../helpers/formAttributes';
import puiFileSearchFilters from './FileSearchFilters';
import puiFileSearchForm from './FileSearchForm';
import puiFileSearchInput from './FileSearchInput';
import puiFileSearchPreview from './FileSearchPreview';
import { AttributeTypes } from '../../../../common/constants';
import { getDamService } from '../../../services';

let searchItemId = 0;

const DETAILS_TAB = {
  itemId: 'details_tab',
  _labelKey: 'dam.search.form.tabs.details.title',
  _attributeFilters: DetailsAttributeFilters,
};

/**
 * Converts the tab's attributes to attribute filters and
 * adds them to '_attributeFilters'
 *
 * @param {object} tab - form tab
 * @returns {object} the same tab
 */
function addAttributeFilters(tab) {
  const attributeFilters = (tab.attributes || []).map(attribute => {
    return {
      alias: attribute.alias,
      itemId: attribute.itemId,
      labelKey: '',
      searchPrefix: '',
      options: {
        multiSelect: false,
        allowGrouping: false,
        formatter: undefined,
        range: false,
      },
      attribute,
      _searchType: attribute.type,
      // Add the field path in which to query for the PiiQL file search
      // => all these attribute filter values are in nested fields of
      // 'Attributes'
      _queryFieldPath: attribute.fieldName ? `Attributes.${attribute.fieldName}` : '',
    };
  });

  tab._attributeFilters = attributeFilters;
  return tab;
}

export default {
  components: {
    puiFileSearchInput,
    puiFileSearchFilters,
    puiFileSearchPreview,
    puiFileSearchForm,
  },
  props: {
    /**
     * Generates the search query for the param search groups,
     * with folder selection built in.
     */
    generateSearchQuery: {
      type: Function,
      required: true,
    },
  },
  data() {
    return {
      // FILTER
      searchFilterReference: null,
      // BOTH
      searchItems: [],
      // OTHER
      filtersAreOpen: false,
      dirtySearchPreview: false,
      // FORM
      piivoForm: null,
    };
  },
  computed: {
    /**
     * @returns {object[]} duplicate array of our search items
     * that the input can freely manipulate
     */
    inputSearchItems() {
      return [...this.searchItems];
    },
    /**
     * @returns {object[]} search items as search objects
     */
    normalizedSearchItems() {
      const items = [];

      for (const rawSearchItem of this.searchItems) {
        const normalizedSearchItem = this.normalizeSearchItem(rawSearchItem);
        if (normalizedSearchItem) {
          items.push(normalizedSearchItem);
        }
      }

      return items;
    },
    /**
     * @returns {boolean} if should show an external search form
     */
    showExternalSearchForm() {
      // TODO: once ext search select is implemented
      return false;
    },
    /**
     * @returns {object[]} the current forms
     */
    forms() {
      return [this.piivoForm].filter(Boolean);
    },
  },
  watch: {
    searchItems: {
      deep: true, // Deep, becuse the .value of an item may change
      /**
       * Watches the prop to reload the search results
       * on change
       */
      handler() {
        this.setDirtySearchPreview();
        this.$emit('searchItemsChange');
      },
    },
  },
  methods: {
    // FILTERS
    piivoTranslate(value) {
      return Vue.filter('piivoTranslate')(value);
    },
    // INPUT
    /**
     * When the user clicks the search button.
     * Triggers a search
     *
     * @param {MouseEvent} event - the click event that caused the
     * search
     * @returns {void}
     */
    inputOnSearchClicked(event) {
      // Prevent this causing the filters to open
      this.$emit('searchClicked', this.normalizedSearchItems);

      // Close filters
      if (this.$refs.filters) {
        this.$refs.filters.close();
      }
    },
    /**
     * When the user presses enter in search bar
     * and there was nothing to save
     *
     * @param {KeyboardEvent} event - the key event that caused the
     * enter
     * @returns {void}
     */
    inputOnEnter(event) {
      this.$emit('inputEnter', this.normalizedSearchItems);
      this.$refs.filters.close();
    },
    /**
     * When the search bar is cleared
     *
     * @param {MouseEvent} event - the click event that caused the
     * clear
     * @returns {void}
     */
    inputOnClearSelection(event) {
      this.searchItems.forEach(item => {
        this.removeSearchItemCorrespondingFilter(item);
      });
      this.searchItems = [];
      this.$emit('clearSelection', this.normalizedSearchItems);

      this.updateFiltersPosition();
    },
    /**
     * Updates our search items to match the new value
     * from the input
     *
     * @param {object[]} items - array of search items
     * @returns {void}
     */
    inputOnAddValue(value, items) {
      this.searchItems = items;

      this.updateFiltersPosition();
    },
    /**
     * Updates our search items to match the new value
     * from the input
     *
     * @param {object} removeItem - the item that was removed
     * @param {object[]} items - array of search items
     * @returns {void}
     */
    inputOnRemoveItem(removedItem, items) {
      this.searchItems = items;
      this.removeSearchItemCorrespondingFilter(removedItem);
      this.$emit('onRemoveItem', this.normalizedSearchItems);

      this.updateFiltersPosition();
    },
    /**
     * Triggers a search if the click that caused the blur
     * was not inside the search input or filter
     *
     * @returns {void}
     */
    inputOnBlur(event) {
      if (!this.$el.contains(event.target)) {
        this.$emit('blur', this.normalizedSearchItems);
      }
    },
    // FILTERS
    /**
     * Emits the 'filtersOpened' event
     *
     * @returns {void}
     */
    filtersOnOpened() {
      this.filtersAreOpen = true;
      this.$emit('filtersOpened', this.normalizedSearchItems);

      // Refresh search in case the search preview became dirty while filters were closed
      this.queueSearchPreviewRefresh();
    },
    /**
     * Emits the 'filtersClosed' event
     *
     * @returns {void}
     */
    filtersOnClosed() {
      this.filtersAreOpen = false;
      this.$emit('filtersClosed', this.normalizedSearchItems);
    },
    /**
     * @returns {string} unique id that can be used for a search item
     */
    generateInputItemId() {
      return `filter_${searchItemId++}`;
    },
    /**
     * Initializes the search filters popper reference
     *
     * @returns {void}
     */
    initSearchFiltersReference() {
      this.searchFilterReference = this.$refs.searchInput.$el;
    },
    /**
     * Duplicates the attribute value array change to our search items
     *
     * @param {object} attributeFilter - the attribute filter object whose value changed
     * @param {array} nextValue - the choices for the attribute value
     * @param {boolean} checkForDeletions - if the value array would have had some
     * items removed, that we should also remove from our search items
     * @returns {void}
     */
    handleAttributeArrayValueChange(attributeFilter, nextValue, checkForDeletions, searchType) {
      const choiceArr = [...nextValue];

      // If there are no more choices selected, just remove the search item
      if (!choiceArr.length) {
        this.searchItems = this.searchItems.filter(searchItem => {
          return (
            !searchItem._attributeFilter ||
            searchItem._attributeFilter.itemId !== attributeFilter.itemId
          );
        });

        return;
      }

      // Otherwise update/add the choices

      const existingSearchItem = this.searchItems.find(
        searchItem =>
          searchItem._attributeFilter &&
          searchItem._attributeFilter.itemId === attributeFilter.itemId
      );

      const formattedValue = this.getSearchItemDisplayValue(attributeFilter, choiceArr);

      // Update the search item values
      if (existingSearchItem) {
        existingSearchItem.value = formattedValue;
        existingSearchItem._valueObj = choiceArr;
      } else {
        // Or create a new one
        this.searchItems.push({
          // Search
          id: this.generateInputItemId(),
          value: formattedValue,
          // Additional
          _isFromAttrFilter: true,
          _isFromArrayAttr: true,
          _attributeFilter: attributeFilter,
          _valueObj: choiceArr,
          _searchSourceType: searchType,
        });
      }
    },
    /**
     * Adds a search item for the newly added attribute value
     *
     * @param {object} attributeFilter - the attribute filter object whose value changed
     * @param {object} attributeValue - the form value object for the attribute
     * @param {string} searchType - the type of search the filtes comes from
     * @returns {void}
     */
    filtersOnAddAttributeValue(attributeFilter, attributeValue, searchType) {
      // If value is array, that means it's an array of choice objects
      // e.g. Link
      // Use the dedicated handler
      if (
        [AttributeTypes.LINKS, AttributeTypes.EXTERNAL_LINK].includes(
          attributeFilter.attribute.type
        )
      ) {
        this.handleAttributeArrayValueChange(attributeFilter, attributeValue, false, searchType);
      } else {
        // Otherwise juste add the value
        const inputValue = this.getSearchItemDisplayValue(attributeFilter, attributeValue);
        this.searchItems.push({
          // Search
          id: this.generateInputItemId(),
          value: inputValue,
          // Additional
          _isFromAttrFilter: true,
          _isFromArrayAttr: false,
          _attributeFilter: attributeFilter,
          _valueObj: attributeValue,
          _searchSourceType: searchType,
        });
      }

      this.updateFiltersPosition();
    },
    /**
     * Updates our search item for the updated attribute filter value
     *
     * @param {object} attributeFilter - the attribute filter object whose value changed
     * @param {object} attributeValue - the form value object for the attribute
     * @param {string} searchType - the type of search the filtes comes from
     * @returns {void}
     */
    filtersOnUpdateAttributeValue(attributeFilter, attributeValue, searchType) {
      const oldSearchItemIndex = this.searchItems.findIndex(
        item => item._attributeFilter && item._attributeFilter.itemId === attributeFilter.itemId
      );

      // If for some reason we don't already have the item,
      // add it instead of updating
      if (oldSearchItemIndex === -1) {
        this.filtersOnAddAttributeValue(attributeFilter, attributeValue);
        return;
      }

      // If the value is an array, use the dedicated handler
      if (
        [AttributeTypes.LINKS, AttributeTypes.EXTERNAL_LINK].includes(
          attributeFilter.attribute.type
        )
      ) {
        this.handleAttributeArrayValueChange(attributeFilter, attributeValue, true, searchType);
      } else {
        // Else simply update the value
        const oldSeachItem = this.searchItems[oldSearchItemIndex];
        const inputValue = this.getSearchItemDisplayValue(attributeFilter, attributeValue);

        oldSeachItem.value = inputValue;
        oldSeachItem._valueObj = attributeValue;
      }

      this.updateFiltersPosition();
    },
    /**
     * @returns {string} the display value for the search item
     */
    getSearchItemDisplayValue(attributeFilter, value) {
      return getSearchItemDisplayValue(attributeFilter, value);
    },
    /**
     * Removes the corresponding filter for the search item
     *
     * @param {object} searchItem - the item whose corresponding filter
     * to remove
     * @returns {void}
     */
    removeSearchItemCorrespondingFilter(searchItem) {
      if (!searchItem._isFromAttrFilter) {
        return;
      }

      const attrId = searchItem._attributeFilter.itemId;

      // Since we aggregate array filters into one search item, when the user clicks
      // the remove button, we can simply remove the whole attribute filter value to each search form
      if (this.$refs.externalFileSearchForm) {
        this.$refs.externalFileSearchForm.removeAttributeValue(attrId);
      }

      if (this.$refs.mediaFileSearchForm) {
        this.$refs.mediaFileSearchForm.removeAttributeValue(attrId);
      }
    },
    /**
     * Triggered when user clicked on a file to search for.
     * Will ask for confirmation before adding the file to the filters
     *
     * @param {object} file - the file to search for
     * @returns {void}
     */
    onSearchForFileTrigger(file) {
      this.$emit('executeConfirmableInfoAction', () => this.onSearchForFile(file));
    },
    /**
     * Adds the filename to the search filters if not already present
     * and emits the 'setFileAsInfoFile' event
     *
     * @param {object} file - the file to search for
     * @returns {void}
     */
    onSearchForFile(file) {
      const searchValue = `${this.$t('dam.constants.search.filename_prefix')}:${file.name}`;

      // Add search item if does not yet exist
      if (!this.searchItems.find(item => item.value === searchValue)) {
        this.searchItems.push({
          id: this.generateInputItemId(),
          value: searchValue,
          _searchSourceType: SearchType.DEFAULT,
        });
      }

      // Close filters
      if (this.$refs.filters) {
        this.$refs.filters.close();
      }

      // No need to emit new search items, since we closed the filters
      // which will trigger an emit of the search items in 'filtersOnClosed'

      this.$emit('setFileAsInfoFile', file);
    },
    /**
     * Updates the filters popper
     *
     * @returns {void}
     */
    updateFiltersPosition() {
      if (this.$refs.filters) {
        this.$refs.filters.updatePopper();
      }
    },
    // EXT FORM
    /**
     * Adds a search item for the newly added attribute value
     *
     * @param {object} attributeFilter - the attribute object whose value changed
     * @param {object} attributeValue - the form value object for the attribute
     * @returns {void}
     */
    extFormOnAddAttributeValue(attributeFilter, attributeValue) {
      this.filtersOnAddAttributeValue(attributeFilter, attributeValue, SearchType.EXTERNAL);
    },
    /**
     * Updates our search item for the updated attribute value
     *
     * @param {object} attributeFilter - the attribute object whose value changed
     * @param {object} attributeValue - the form value object for the attribute
     * @returns {void}
     */
    extFormOnUpdateAttributeValue(attributeFilter, attributeValue) {
      this.filtersOnUpdateAttributeValue(attributeFilter, attributeValue, SearchType.EXTERNAL);
    },
    /**
     * Retrieves the form definition for the selected
     * external search
     *
     * @returns {Promise<object>}
     */
    // eslint-disable-next-line @typescript-eslint/require-await
    async retrieveExtSearchFormDef() {
      // TODO:
      throw new Error('not yet implemented');
    },
    // PIIVO FORM
    /**
     * Adds a search item for the newly added attribute value
     *
     * @param {object} attributeFilter - the attribute object whose value changed
     * @param {object} attributeValue - the form value object for the attribute
     * @returns {void}
     */
    piivoFormOnAddAttributeValue(attributeFilter, attributeValue) {
      this.filtersOnAddAttributeValue(attributeFilter, attributeValue, SearchType.DEFAULT);
    },
    /**
     * Updates our search item for the updated attribute value
     *
     * @param {object} attributeFilter - the attribute object whose value changed
     * @param {object} attributeValue - the form value object for the attribute
     * @returns {void}
     */
    piivoFormOnUpdateAttributeValue(attributeFilter, attributeValue) {
      this.filtersOnUpdateAttributeValue(attributeFilter, attributeValue, SearchType.DEFAULT);
    },
    /**
     * Retrieves the form definition for the default media search
     *
     * @returns {Promise<object>}
     */
    async retrievePiivoFormDef() {
      const formRes = await getDamService('filesService').getGlobalForm('mediaSearchFilters');
      const form = formRes.body;

      form.tabs = form.tabs || [];

      // Map the attributes from the form to attribute filters
      form.tabs.map(tab => addAttributeFilters(tab));

      // Add the details filter tab as last tab
      form.tabs.push(DETAILS_TAB);

      this.piivoForm = form;
      return form;
    },
    // FORM > ANY
    /**
     * @param {string} value - the search item value
     * @returns {boolean} if the search item value is a valid search item
     */
    isSearchItemValid(value) {
      if (!isSearchItemInlineFilter(value)) {
        return true;
      }

      const { name: attributeName } = extractSearchItemAttributeValue(value);

      return (
        isFilenameSearchPrefix(attributeName) ||
        checkAttributeFilterExists(this.forms, attributeName)
      );
    },
    /**
     * Normalizes a search item, to a search item object that can be handled by fileSearchHelper
     *
     * @param {object} item - a raw search item
     * @returns {object|null} - a search item object or null of not searchable
     */
    normalizeSearchItem(item) {
      if (item._isFromAttrFilter) {
        return item;
      }
      // Else is from user manual input

      if (!isSearchItemInlineFilter(item.value)) {
        return item;
      }
      // Else is attribute-value search item

      const { name: attributeName, value: attributeValue } = extractSearchItemAttributeValue(
        item.value
      );
      if (isFilenameSearchPrefix(attributeName)) {
        // Return item as is, it can be handled by fileSearchHelper
        return item;
      }

      const attributeFilter = getCorrespondingAttributeFilter(this.forms, attributeName);
      if (!attributeFilter) {
        // If attribute not found, the search item cannot be used
        return null;
      }

      return {
        id: item.id,
        value: item.value,
        // Additional
        _isFromAttrFilter: false,
        _isFromArrayAttr: false,
        _attributeFilter: attributeFilter,
        _valueObj: attributeValue,
        _searchSourceType: null,
      };
    },
    // EXPOSED
    /**
     * Marks the search preview as dirty.
     */
    setDirtySearchPreview() {
      this.dirtySearchPreview = true;
      this.queueSearchPreviewRefresh();
    },
    /**
     * Only if applicable and if possible: triggers a search preview refresh
     */
    queueSearchPreviewRefresh() {
      if (this.dirtySearchPreview && this.filtersAreOpen && this.$refs.searchPreview) {
        this.$refs.searchPreview.refresh();
      }
    },
  },
  /**
   * Mounted hook. Init any variables
   *
   * @returns {void}
   */
  mounted() {
    this.initSearchFiltersReference();
  },
};
</script>
